/* SOVEREIGN motion + popup (plan WP3.11). Vanilla, deferred, idempotent. */
(() => {
  if (window.sovMotion) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Scroll reveals: only elements below the fold start hidden, so nothing above the fold flashes.
     Product cards and tiles are revealed with an 80 ms stagger within their row. */
  let io = null;
  const CARD_SEL = '.product-grid__card, .resource-list__item, .sov-tile';
  const reveal = (root = document) => {
    if (reduce.matches || !('IntersectionObserver' in window)) return;
    if (!io) {
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('sov-reveal-in');
          e.target.classList.remove('sov-reveal-pending');
          io.unobserve(e.target);
        }
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
    }
    const fold = window.innerHeight;
    root.querySelectorAll(CARD_SEL).forEach((el) => {
      if (el.hasAttribute('data-sov-reveal') || el.closest('.sov-hero, .sov-offer')) return;
      el.setAttribute('data-sov-reveal', '');
      const sibs = el.parentElement ? Array.from(el.parentElement.children) : [];
      el.style.setProperty('--sov-reveal-delay', ((sibs.indexOf(el) % 5) * 80) + 'ms');
    });
    root.querySelectorAll('[data-sov-reveal]:not(.sov-reveal-in):not(.sov-reveal-pending)').forEach((el) => {
      if (el.closest('.sov-hero--lead')) return;
      if (el.getBoundingClientRect().top < fold) return;
      el.classList.add('sov-reveal-pending');
      io.observe(el);
    });
  };

  /* Ken Burns starts when a banner is actually on screen (not while it is below the fold). */
  let kbIo = null;
  const kenburns = (root = document) => {
    const els = root.querySelectorAll('.sov-kenburns:not(.sov-kb-run)');
    if (reduce.matches) return;
    if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('sov-kb-run')); return; }
    if (!kbIo) {
      kbIo = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.add('sov-kb-run');
          kbIo.unobserve(e.target);
        }
      }, { threshold: 0.25 });
    }
    els.forEach((el) => kbIo.observe(el));
  };

  /* Product card hover: the first image stays visible; the second image is decoded ahead of time and
     cross-fades in (400 ms) only once it is ready, so there is never a blank frame. */
  const hoverPrepared = new WeakSet();
  const prepareCard = (gal) => {
    if (hoverPrepared.has(gal)) return;
    const imgs = gal.querySelectorAll('slideshow-slide img');
    if (imgs.length < 2) return;
    hoverPrepared.add(gal);
    const first = imgs[0];
    const second = imgs[1];
    const layers = document.createElement('div');
    layers.className = 'sov-layers';
    layers.setAttribute('aria-hidden', 'true');
    const base = new Image();
    base.alt = '';
    base.decoding = 'async';
    const hover = new Image();
    hover.alt = '';
    hover.className = 'sov-layer-hover';
    hover.decoding = 'async';
    layers.append(base, hover);
    gal.append(layers);
    let ready = false;
    let hovering = false;
    let started = false;
    const show = () => { if (ready && hovering) layers.classList.add('sov-on'); };
    const start = () => {
      if (started) return;
      started = true;
      const w = Math.round(gal.clientWidth) || 300;
      base.sizes = hover.sizes = w + 'px';
      base.srcset = first.srcset || '';
      base.src = first.currentSrc || first.src;
      hover.srcset = second.srcset || '';
      hover.fetchPriority = 'low';
      hover.src = second.getAttribute('src') || '';
      const done = () => { ready = true; show(); };
      (hover.decode ? hover.decode() : Promise.resolve()).then(done, done);
    };
    gal.addEventListener('pointerenter', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      hovering = true;
      start();
      layers.classList.add('sov-live');
      show();
    });
    gal.addEventListener('pointerleave', () => {
      hovering = false;
      layers.classList.remove('sov-on');
      window.setTimeout(() => { if (!hovering) layers.classList.remove('sov-live'); }, reduce.matches ? 0 : 650);
    });
    gal.sovWarm = start;
  };
  let warmIo = null;
  const cards = (root = document) => {
    root.querySelectorAll('.card-gallery').forEach(prepareCard);
    if (!('IntersectionObserver' in window)) return;
    if (!warmIo) {
      warmIo = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          warmIo.unobserve(e.target);
          const warm = () => e.target.sovWarm && e.target.sovWarm();
          if ('requestIdleCallback' in window) window.requestIdleCallback(warm, { timeout: 2500 });
          else window.setTimeout(warm, 1200);
        }
      }, { rootMargin: '120px' });
    }
    root.querySelectorAll('.card-gallery').forEach((g) => { if (g.sovWarm) warmIo.observe(g); });
  };

  /* PDP zoom dialog: opens with the whole portrait fitted to the screen (CSS); from there click /
     scroll-wheel / pinch / double-tap magnify up to 4x, and the pointer or a finger pans. */
  const zoomLi = (dlg, li, hint) => {
    const MAXS = 4;
    const st = { s: 1, tx: 0, ty: 0, geo: null };
    const img = () => li.querySelector('img.product-media__image, .product-media img');
    const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
    const geo = () => {
      const im = img();
      if (!im) return null;
      if (st.geo && st.geo.im === im && st.s === 1) { return st.geo; }
      if (st.s === 1 && st.tx === 0) {
        const r = im.getBoundingClientRect();
        if (r.width > 0) st.geo = { im, cx: r.left + r.width / 2, cy: r.top + r.height / 2, w: im.offsetWidth, h: im.offsetHeight };
      }
      return st.geo;
    };
    const apply = () => {
      li.style.setProperty('--sov-s', String(st.s));
      li.style.setProperty('--sov-tx', st.tx + 'px');
      li.style.setProperty('--sov-ty', st.ty + 'px');
      li.classList.toggle('sov-zoomed', st.s > 1.001);
      dlg.classList.toggle('sov-zoom-active', st.s > 1.001);
    };
    const bound = (g) => {
      const mx = ((st.s - 1) * g.w) / 2;
      const my = ((st.s - 1) * g.h) / 2;
      st.tx = clamp(st.tx, -mx, mx);
      st.ty = clamp(st.ty, -my, my);
    };
    const reset = () => { st.s = 1; st.tx = 0; st.ty = 0; st.geo = null; li.classList.remove('sov-panning'); apply(); };
    /* Zoom to scale ns keeping the image point under (px, py) fixed on screen. */
    const zoomAt = (px, py, ns) => {
      const g = geo();
      if (!g) return;
      ns = clamp(ns, 1, MAXS);
      if (ns <= 1.001) { reset(); return; }
      const ux = (px - g.cx - st.tx) / st.s;
      const uy = (py - g.cy - st.ty) / st.s;
      st.s = ns;
      st.tx = px - g.cx - ns * ux;
      st.ty = py - g.cy - ns * uy;
      bound(g);
      apply();
    };
    /* Pointer-follow pan used on desktop once magnified. */
    const follow = (px, py) => {
      const g = geo();
      if (!g || st.s <= 1.001) return;
      st.tx = -(st.s - 1) * clamp(px - g.cx, -g.w / 2, g.w / 2);
      st.ty = -(st.s - 1) * clamp(py - g.cy, -g.h / 2, g.h / 2);
      apply();
    };
    const overImage = (x, y) => {
      const im = img();
      if (!im) return false;
      const g = st.geo;
      if (g && st.s === 1) return Math.abs(x - g.cx) <= g.w / 2 && Math.abs(y - g.cy) <= g.h / 2;
      const r = im.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    };

    /* Horizon closes the dialog on any click of the slide; here only clicks outside the photo close it. */
    li.removeAttribute('on:click');
    li.addEventListener('click', (e) => {
      if (!overImage(e.clientX, e.clientY)) {
        const x = dlg.querySelector('.dialog-zoomed-gallery__close-button');
        if (x) x.click();
        return;
      }
      e.stopPropagation();
      if (e.detail > 1 && e.pointerType === 'touch') return;
      if (st.s > 1.001) { reset(); return; }
      zoomAt(e.clientX, e.clientY, 2.5);
      follow(e.clientX, e.clientY);
    }, true);
    li.addEventListener('mousemove', (e) => {
      if (st.s <= 1.001) return;
      li.classList.add('sov-panning');
      follow(e.clientX, e.clientY);
    });
    li.addEventListener('mouseleave', () => li.classList.remove('sov-panning'));
    li.addEventListener('wheel', (e) => {
      if (!overImage(e.clientX, e.clientY)) return;
      e.preventDefault();
      li.classList.remove('sov-panning');
      zoomAt(e.clientX, e.clientY, st.s * Math.exp(-e.deltaY * 0.0022));
      if (st.s > 1.001) follow(e.clientX, e.clientY);
    }, { passive: false });

    /* Touch: pinch, drag, double-tap. Captured so Horizon's own 1.5x-start handler never runs. */
    let pinch = null;
    let drag = null;
    let lastTap = { t: 0, x: 0, y: 0 };
    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    li.addEventListener('touchstart', (e) => {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      li.classList.add('sov-panning');
      if (e.touches.length === 2) {
        const [a, b] = e.touches;
        pinch = { d: dist(a, b), s: st.s };
        drag = null;
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        const now = performance.now();
        if (now - lastTap.t < 300 && Math.hypot(t.clientX - lastTap.x, t.clientY - lastTap.y) < 50) {
          lastTap.t = 0;
          li.classList.remove('sov-panning');
          if (st.s > 1.001) reset(); else zoomAt(t.clientX, t.clientY, 2.5);
          return;
        }
        lastTap = { t: now, x: t.clientX, y: t.clientY };
        drag = { x: t.clientX, y: t.clientY, tx: st.tx, ty: st.ty };
      }
    }, { capture: true, passive: false });
    li.addEventListener('touchmove', (e) => {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      if (e.touches.length === 2 && pinch) {
        const [a, b] = e.touches;
        zoomAt((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2, (pinch.s * dist(a, b)) / pinch.d);
      } else if (e.touches.length === 1 && drag && st.s > 1.001) {
        const g = geo();
        st.tx = drag.tx + (e.touches[0].clientX - drag.x);
        st.ty = drag.ty + (e.touches[0].clientY - drag.y);
        if (g) bound(g);
        apply();
      }
    }, { capture: true, passive: false });
    li.addEventListener('touchend', (e) => {
      e.stopPropagation();
      if (e.touches.length === 0) { pinch = null; drag = null; li.classList.remove('sov-panning'); }
      else if (e.touches.length === 1) { pinch = null; const t = e.touches[0]; drag = { x: t.clientX, y: t.clientY, tx: st.tx, ty: st.ty }; }
    }, { capture: true, passive: false });
    dlg.addEventListener('close', reset);
    window.addEventListener('resize', () => { st.geo = null; reset(); });
    li.sovReset = reset;
    li.sovZoomAt = zoomAt;
  };
  const zoom = () => {
    document.querySelectorAll('zoom-dialog dialog:not([data-sov-zoom])').forEach((dlg) => {
      dlg.dataset.sovZoom = '1';
      const hint = document.createElement('div');
      hint.className = 'sov-zoom-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.textContent = window.matchMedia('(hover: none)').matches ? 'Pinch or double-tap to zoom' : 'Click or scroll to magnify';
      dlg.append(hint);
      dlg.querySelectorAll('.dialog-zoomed-gallery li.product-media-container--image').forEach((li) => zoomLi(dlg, li, hint));
    });
  };

  /* Below-the-fold art waits for the window load event, then loads just ahead of the viewport. */
  let lazyIo = null;
  let pageLoaded = document.readyState === 'complete';
  const restore = (img) => {
    const pic = img.closest('picture');
    if (pic) pic.querySelectorAll('source[data-srcset]').forEach((s) => { s.srcset = s.dataset.srcset; s.removeAttribute('data-srcset'); });
    if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
    if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
    img.loading = 'eager';
    img.removeAttribute('data-sov-lazy');
  };
  const lazyArt = (root = document) => {
    const imgs = root.querySelectorAll('img[data-sov-lazy]');
    if (!imgs.length) return;
    if (!pageLoaded) return;
    if (!('IntersectionObserver' in window)) { imgs.forEach(restore); return; }
    if (!lazyIo) {
      lazyIo = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          lazyIo.unobserve(e.target);
          restore(e.target);
        }
      }, { rootMargin: '600px 0px' });
    }
    imgs.forEach((i) => lazyIo.observe(i));
  };
  if (!pageLoaded) window.addEventListener('load', () => { pageLoaded = true; window.setTimeout(() => lazyArt(), 150); }, { once: true });

  /* Founding Edit popup: once per visitor (localStorage), Esc / close button / backdrop click. */
  const KEY = 'sov-founding-seen';
  const store = {
    get() { try { return Number(localStorage.getItem(KEY)) || 0; } catch (e) { return 0; } },
    set() { try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* private mode */ } },
  };
  const popup = (root = document) => {
    const dlg = root.querySelector('[data-sov-popup]');
    if (!dlg || dlg.dataset.bound || typeof dlg.showModal !== 'function') return;
    dlg.dataset.bound = '1';
    const close = () => { dlg.close(); store.set(); };
    dlg.querySelectorAll('[data-sov-popup-close]').forEach((b) => b.addEventListener('click', close));
    dlg.addEventListener('click', (e) => { if (e.target === dlg) close(); });
    dlg.addEventListener('cancel', () => store.set());
    const success = dlg.querySelector('[data-sov-popup-success]');
    if (success && location.hash === '#' + dlg.dataset.formId) { dlg.showModal(); store.set(); return; }
    if (dlg.hasAttribute('data-design-mode')) return;
    if (/^\/(checkout|cart|account|password)/.test(location.pathname)) return;
    const days = Number(dlg.dataset.days) || 30;
    if (Date.now() - store.get() < days * 864e5) return;
    window.setTimeout(() => { if (!document.querySelector('dialog[open]')) { dlg.showModal(); store.set(); } }, (Number(dlg.dataset.delay) || 0) * 1000);
  };

  const init = (root) => { reveal(root); kenburns(root); cards(root); lazyArt(root); popup(root); zoom(); };
  window.sovMotion = { init };
  /* Cards / grids that arrive later (filters, pagination, section re-render) get the same treatment. */
  if ('MutationObserver' in window) {
    let t = 0;
    new MutationObserver(() => { window.clearTimeout(t); t = window.setTimeout(() => { reveal(); cards(); }, 120); })
      .observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
